import React, { useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    Divider,
    Chip,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    List,
    ListItem,
    IconButton,
    LinearProgress,
} from "@mui/material";
import {
    HelpCircle,
    Clock,
    ArrowLeft,
    Settings,
    MoreVertical,
    Flag,
    CheckCircle,
    XCircle,
    PlayCircle,
    Timer,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

const QuizView = () => {
    const navigate = useNavigate();
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState<Record<number, string>>({});
    const [submitted, setSubmitted] = useState(false);

    const quizInfo = {
        title: "Practice Quiz: De Moivre's Theorem",
        course: "Advanced Algebra & Calculus",
        timeLimit: "30:00",
        totalQuestions: 15,
    };

    const questions = [
        {
            id: 1,
            text: "If z = 2(cos π/3 + i sin π/3), what is z⁶ using De Moivre's Theorem?",
            options: [
                "64",
                "64(cos 2π + i sin 2π)",
                "12(cos 2π + i sin 2π)",
                "32",
            ],
            correct: "64",
        },
        {
            id: 2,
            text: "Express (1 + i)¹⁰ in the form a + bi.",
            options: [
                "32i",
                "1024i",
                "32",
                "512i",
            ],
            correct: "32i",
        },
    ];

    const handleNext = () => {
        if (currentQuestion < questions.length - 1) {
            setCurrentQuestion(currentQuestion + 1);
        }
    };

    const handlePrev = () => {
        if (currentQuestion > 0) {
            setCurrentQuestion(currentQuestion - 1);
        }
    };

    const handleAnswerSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAnswers({ ...answers, [currentQuestion]: e.target.value });
    };

    const isCompleted = Object.keys(answers).length === questions.length;

    return (
        <Box sx={{ p: 4, bgcolor: "#f1f5f9", minHeight: "100vh" }}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {/* Header Navigation */}
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4, alignItems: "center" }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b", mb: 0.5 }}>{quizInfo.title}</Typography>
                        <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600 }}>{quizInfo.course}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        <Paper sx={{ px: 2, py: 1, borderRadius: "12px", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Clock size={20} color="#ef4444" />
                            <Typography variant="h6" sx={{ fontWeight: 800, color: "#ef4444" }}>24:18</Typography>
                        </Paper>
                        <IconButton sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0" }}><Settings size={20} /></IconButton>
                    </Box>
                </Box>

                <Grid container spacing={4}>
                    <Grid item xs={12} lg={8}>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentQuestion}
                                initial={{ x: 20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                exit={{ x: -20, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                <Paper sx={{ p: 5, borderRadius: "32px", border: "1px solid #e2e8f0", minHeight: "450px", position: "relative" }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
                                        <Chip
                                            label={`Question ${currentQuestion + 1} of ${questions.length}`}
                                            sx={{ bgcolor: "#4f46e5", color: "#fff", fontWeight: 800, borderRadius: "8px" }}
                                        />
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                            <IconButton size="small"><Flag size={18} color="#94a3b8" /></IconButton>
                                            <IconButton size="small"><MoreVertical size={18} /></IconButton>
                                        </Box>
                                    </Box>

                                    <Typography variant="h5" sx={{ mb: 6, fontWeight: 700, color: "#1e293b", lineHeight: 1.6 }}>
                                        {questions[currentQuestion].text}
                                    </Typography>

                                    <FormControl fullWidth>
                                        <RadioGroup value={answers[currentQuestion] || ""} onChange={handleAnswerSelect}>
                                            {questions[currentQuestion].options.map((option, idx) => (
                                                <Paper
                                                    key={idx}
                                                    sx={{
                                                        mb: 2,
                                                        borderRadius: "16px",
                                                        border: "2px solid",
                                                        borderColor: answers[currentQuestion] === option ? "#4f46e5" : "#f1f5f9",
                                                        bgcolor: answers[currentQuestion] === option ? "#f5f3ff" : "#fff",
                                                        transition: "all 0.2s",
                                                        cursor: "pointer",
                                                        "&:hover": { borderColor: "#4f46e5" }
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        value={option}
                                                        control={<Radio sx={{ color: "#e2e8f0", "&.Mui-checked": { color: "#4f46e5" } }} />}
                                                        label={option}
                                                        sx={{ width: "100%", m: 0, p: 2, py: 2.5, "& .MuiTypography-root": { fontWeight: 600, fontSize: "1.05rem", color: "#1e293b" } }}
                                                    />
                                                </Paper>
                                            ))}
                                        </RadioGroup>
                                    </FormControl>

                                    <Box sx={{ mt: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <Button
                                            startIcon={<ArrowLeft size={18} />}
                                            onClick={handlePrev}
                                            disabled={currentQuestion === 0}
                                            sx={{ textTransform: "none", color: "#64748b", fontWeight: 700 }}
                                        >
                                            Previous
                                        </Button>
                                        {currentQuestion < questions.length - 1 ? (
                                            <Button
                                                variant="contained"
                                                endIcon={<ArrowRight size={18} />}
                                                onClick={handleNext}
                                                sx={{ bgcolor: "#4f46e5", color: "#fff", borderRadius: "12px", px: 4, py: 1.2, fontWeight: 800, boxShadow: "0 6px 14px rgba(79, 70, 229, 0.35)" }}
                                            >
                                                Next Question
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="contained"
                                                endIcon={<CheckCircle size={18} />}
                                                onClick={() => setSubmitted(true)}
                                                sx={{ bgcolor: "#10b981", color: "#fff", borderRadius: "12px", px: 4, py: 1.2, fontWeight: 800, boxShadow: "0 6px 14px rgba(16, 185, 129, 0.35)" }}
                                            >
                                                Finish Attempt
                                            </Button>
                                        )}
                                    </Box>
                                </Paper>
                            </motion.div>
                        </AnimatePresence>
                    </Grid>

                    {/* Quiz Navigator Sidebar */}
                    <Grid item xs={12} lg={4}>
                        <Paper sx={{ p: 4, borderRadius: "32px", border: "1px solid #e2e8f0", mb: 4 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, mb: 3 }}>Quiz Navigation</Typography>
                            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 1.5, mb: 4 }}>
                                {Array.from({ length: questions.length }).map((_, idx) => (
                                    <Box
                                        key={idx}
                                        onClick={() => setCurrentQuestion(idx)}
                                        sx={{
                                            width: "100%",
                                            aspectRatio: "1",
                                            borderRadius: "10px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: idx === currentQuestion ? "#4f46e5" : (answers[idx] ? "#eef2ff" : "#fff"),
                                            color: idx === currentQuestion ? "#fff" : (answers[idx] ? "#4f46e5" : "#64748b"),
                                            border: "2px solid",
                                            borderColor: idx === currentQuestion ? "#4f46e5" : (answers[idx] ? "#c7d2fe" : "#e2e8f0"),
                                            fontWeight: 800,
                                            fontSize: "0.9rem",
                                            cursor: "pointer",
                                            transition: "all 0.1s",
                                            "&:hover": { borderColor: "#4f46e5" }
                                        }}
                                    >
                                        {idx + 1}
                                    </Box>
                                ))}
                            </Box>
                            <Divider sx={{ mb: 3 }} />
                            <Button
                                fullWidth
                                sx={{ textTransform: "none", color: "#ef4444", fontWeight: 700 }}
                                onClick={() => navigate(-1)}
                            >
                                Finish attempt...
                            </Button>
                        </Paper>

                        <Paper sx={{ p: 4, borderRadius: "32px", border: "1px solid #e2e8f0" }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Completion</Typography>
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b" }}>Progress Bar</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: "#1e293b" }}>{Math.round((Object.keys(answers).length / questions.length) * 100)}%</Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={(Object.keys(answers).length / questions.length) * 100}
                                    sx={{ height: 8, borderRadius: 4, bgcolor: "#f1f5f9", "& .MuiLinearProgress-bar": { bgcolor: "#10b981", borderRadius: 4 } }}
                                />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </motion.div>
        </Box>
    );
};

export default QuizView;

const ArrowRight = ({ size, color }: { size: number, color?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
    </svg>
);
